import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getLawyers } from "../../services/LawyerService";
import { downloadFilePost } from "../../services/ReportService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import { useToast } from "../../context/ToastContext";

export default function ReportLegalFileBasic() {
  const { showToast } = useToast();
  // 🔹 Filtros por updated_at
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // 🔹 Nuevos filtros por start_date (fecha de inicio del expediente)
  const [startDateFile, setStartDateFile] = useState(null);
  const [endDateFile, setEndDateFile] = useState(null);

  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchLawyers() {
      setLoadingOptions(true);
      try {
        const resLawyers = await getLawyers();
        setLawyers(resLawyers.data.data || []);
      } catch (error) {
        console.error("Error al cargar abogados:", error);
        showToast("Error al cargar abogados.", "error")
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchLawyers();
  }, [showToast]);

  const handleGenerateReport = async () => {
    if (startDate && !endDate) {
      showToast("Debe completar la fecha 'Hasta' de actualización.", "error")
      return;
    }
    if (startDateFile && !endDateFile) {
      showToast("Debe completar la fecha 'Hasta' del inicio del expediente.", "error")
      return;
    }
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast(
        "La fecha de actualización inicial no puede ser posterior a la final.",
      "error")
      return;
    }
    if (startDateFile && endDateFile && startDateFile.isAfter(endDateFile)) {
      showToast(
        "La fecha de inicio del expediente no puede ser posterior a la final.",
      "error")
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lawyers: selectedLawyers,
        legal_file_stage_ids: selectedStages.map((s) => s.id),
        legal_file_types: selectedTypes.map((t) => t.id),
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      // 🔹 Filtros por fecha de actualización
      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      // 🔹 Filtros por fecha de inicio del expediente
      if (startDateFile)
        payload.start_date_file = startDateFile.format("YYYY-MM-DD");
      if (endDateFile) payload.end_date_file = endDateFile.format("YYYY-MM-DD");

      const response = await downloadFilePost(
        "legal/file/report/basic",
        payload,
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `reporte_expedientes_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
            <CardHeader
              icon={<AssignmentIcon />}
              title="Inventario Expedientes"
              description="Exporta el inventario de expedientes con sus datos esenciales,
                incluyendo filtros por fecha de actualización o fecha de inicio."
            />

            <Grid container spacing={2}>
              {/* 🔹 Filtros por fecha de actualización */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              {/* 🔹 Nuevos filtros por fecha de inicio del expediente */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Fecha Incio expediente desde"
                  value={startDateFile}
                  onChange={(newValue) => setStartDateFile(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Fecha Inicio expediente hasta"
                  value={endDateFile}
                  onChange={(newValue) => setEndDateFile(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              {/* 🔹 Filtro por etapa */}
              <Grid size={{ xs: 12 }}>
                <LegalFileStageFilter
                  value={selectedStages}
                  onChange={setSelectedStages}
                />
              </Grid>

              {/* 🔹 Filtro por tipo */}
              <Grid size={{ xs: 12 }}>
                <LegalFileTypeFilter
                  value={selectedTypes}
                  onChange={setSelectedTypes}
                />
              </Grid>

              {/* 🔹 Filtro por estado del título */}
              <Grid size={{ xs: 12 }}>
                <LegalFileStatusTaxFilter
                  value={selectedStatusTax}
                  onChange={setSelectedStatusTax}
                />
              </Grid>

              {/* 🔹 Filtro por abogados */}
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={[
                    {
                      id: "select-all",
                      name: "Seleccionar todos",
                      lastname: "",
                    },
                    ...lawyers,
                  ]}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.id === "select-all"
                      ? option.name
                      : `${option.name} ${option.lastname}` || option
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedLawyers}
                  onChange={(event, newValue) => {
                    const selectAllOption = newValue.find(
                      (val) => val.id === "select-all",
                    );
                    if (selectAllOption) {
                      if (selectedLawyers.length === lawyers.length) {
                        setSelectedLawyers([]);
                      } else {
                        setSelectedLawyers(lawyers);
                      }
                    } else {
                      setSelectedLawyers(newValue);
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    const isSelectAll = option.id === "select-all";
                    const allSelected =
                      selectedLawyers.length === lawyers.length;
                    return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={isSelectAll ? allSelected : selected}
                        />
                        {isSelectAll
                          ? option.name
                          : `${option.name} ${option.lastname}` || option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Abogados"
                      placeholder="Seleccionar..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* 🔹 Botón exportar */}
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleGenerateReport}
                 
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Reporte Expedientes"
                  )}
                </Button>
              </Grid>
            </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
